import { useEffect, useRef, type RefObject } from 'react';

/**
 * Close a popover on outside pointer-down and on Escape.
 *
 * LIFTED OUT OF `TopBar.tsx` so the global search panel can share it. It was
 * private to that file and the search box needed exactly the same behaviour;
 * copying it would have given the shell two dismiss implementations that could
 * drift, and "why does the search panel not close on outside click but the bell
 * tray does" is precisely the bug that produces.
 *
 * ONE SUBTLETY WORTH KNOWING BEFORE YOU REUSE THIS: the Escape listener is on
 * `document`, so EVERY open popover using this hook closes on EVERY Escape,
 * wherever focus happens to be. That is the right default for a set of mutually
 * exclusive chrome popovers. It is the wrong default for a component that wants
 * Escape to mean something more specific — the search combobox, for instance,
 * closes its own panel and must NOT also collapse the notification tray behind
 * it. Such a component handles Escape in its own React `onKeyDown` and calls
 * `stopPropagation()`, which prevents the native event from ever reaching this
 * document listener. See `GlobalSearch.tsx`.
 */
export function useDismiss(open: boolean, close: () => void): RefObject<HTMLDivElement> {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent): void => {
      if (ref.current !== null && !ref.current.contains(event.target as Node)) close();
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') close();
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close]);

  return ref;
}
